import React, { useEffect, useState } from "react";
import { Eye, Heart, Home, Users, Search, Building2, TrendingUp, Clock, Ban, ShieldCheck } from "lucide-react";
import { AdminShell } from "./AdminShell";
import { adminApi, mediaUrl } from "./adminApi";
import { fmtPrice } from "../api";

const KPI = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-[#111114] border border-white/10 p-5" data-testid={`kpi-${label}`}>
    <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider"><Icon className="w-4 h-4 text-[#C19A6B]" /> {label}</div>
    <div className="text-3xl font-light text-white mt-2">{value}</div>
    {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
  </div>
);

const BarList = ({ title, icon: Icon, items, max }) => (
  <div className="bg-[#111114] border border-white/10 p-5">
    <div className="flex items-center gap-2 text-sm text-white mb-4"><Icon className="w-4 h-4 text-[#C19A6B]" /> {title}</div>
    {(!items || items.length === 0) ? (
      <p className="text-xs text-zinc-500">Aucune donnée pour le moment.</p>
    ) : (
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i}>
            <div className="flex justify-between text-xs text-zinc-300 mb-1"><span className="truncate pr-2">{it.label}</span><span className="text-[#C19A6B] shrink-0">{it.count}</span></div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-[#C19A6B]" style={{ width: `${Math.max(6, (it.count / (max || 1)) * 100)}%` }} /></div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const RankTable = ({ title, icon: Icon, rows, metric }) => (
  <div className="bg-[#111114] border border-white/10 p-5">
    <div className="flex items-center gap-2 text-sm text-white mb-4"><Icon className="w-4 h-4 text-[#C19A6B]" /> {title}</div>
    {(!rows || rows.length === 0) ? <p className="text-xs text-zinc-500">Aucune donnée.</p> : (
      <div className="divide-y divide-white/5">
        {rows.map((r) => (
          <a key={r.slug} href={`/bien/${r.slug}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 py-2.5 hover:bg-white/5 transition-colors" data-testid={`rank-${r.slug}`}>
            {r.cover ? <img src={mediaUrl(r.cover)} alt="" className="w-12 h-9 object-cover shrink-0" /> : <div className="w-12 h-9 bg-white/5 shrink-0" />}
            <span className="flex-1 text-sm text-zinc-200 truncate">{r.title}</span>
            <span className="text-sm text-[#C19A6B] shrink-0">{metric === "views" ? `${r.views} vues` : `${r.favorites} ❤`}</span>
          </a>
        ))}
      </div>
    )}
  </div>
);

export default function Stats({ embedded } = {}) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { adminApi.stats().then(setS).catch((e) => setErr(e.message || "Accès refusé")); }, []);

  if (err) return <AdminShell embedded={embedded} title="Statistiques"><p className="text-red-400 text-sm" data-testid="stats-error">{err} — réservé au super administrateur.</p></AdminShell>;
  if (!s) return <AdminShell embedded={embedded} title="Statistiques"><p className="text-zinc-500 text-sm">Chargement…</p></AdminShell>;

  const L = s.listings, M = s.members;
  const maxCity = Math.max(1, ...(s.top_cities || []).map((x) => x.count));
  const maxType = Math.max(1, ...(s.top_types || []).map((x) => x.count));
  const maxAdv = Math.max(1, ...(s.listings_per_advisor || []).map((x) => x.count));
  const maxMonth = Math.max(1, ...(s.affiliations_by_month || []).map((x) => x.count));

  return (
    <AdminShell embedded={embedded} title="Statistiques du réseau">
      <div className="space-y-8" data-testid="stats-page">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI icon={Home} label="Biens" value={L.total} sub={`${L.online} en ligne · ${L.offline} hors ligne`} />
          <KPI icon={Eye} label="Vues totales" value={L.views} />
          <KPI icon={Heart} label="Favoris" value={L.favorites} />
          <KPI icon={Search} label="Recherches" value={s.total_searches} />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI icon={ShieldCheck} label="Conseillers validés" value={M.approved || 0} />
          <KPI icon={Clock} label="En attente" value={M.pending || 0} />
          <KPI icon={Ban} label="Bloqués" value={M.blocked || 0} />
          <KPI icon={Users} label="Refusés" value={M.rejected || 0} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RankTable title="Biens les plus vus" icon={Eye} rows={s.top_viewed} metric="views" />
          <RankTable title="Biens les plus mis en favori" icon={Heart} rows={s.top_favorited} metric="favorites" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <BarList title="Villes les plus recherchées" icon={TrendingUp} items={s.top_cities} max={maxCity} />
          <BarList title="Types les plus recherchés" icon={TrendingUp} items={s.top_types} max={maxType} />
          <BarList title="Biens par conseiller" icon={Building2} items={(s.listings_per_advisor || []).map((x) => ({ label: x.advisor, count: x.count }))} max={maxAdv} />
        </div>

        <BarList title="Demandes d'affiliation par mois" icon={Users} items={(s.affiliations_by_month || []).map((x) => ({ label: x.month, count: x.count }))} max={maxMonth} />
      </div>
    </AdminShell>
  );
}
